import { MstGenericResponse, checkAxiosResponse, extractValidationError } from '@mst-fe/shared';
import axios from 'axios';

import { ConfigSchemaFormModel } from '../../_shared/models/config-schema-form-model';
import { LONG_REQUEST_TIMEOUT } from '../../constants';
import { observeEndpoint } from '../../services/endpoint-observer';
import { axiosOptions } from '../../services/requests';
import { EditJobModel } from './models/job-edit';

export interface Cluster {
  id: string;
  name: string;
}

export interface JobListItem {
  id: string;
  name: string;
  crontab: string;
  description: string;
  clusterId: string;
  clusterName: string;
  enabled: boolean;
  wbShortId: string | undefined;
  wbName: string | undefined;
  installedModuleId: string | undefined;
  publishedModuleId: string | undefined;
  manifestId: string | undefined;
  logo: string | undefined;
}

export interface JobExecution {
  id: string;
  jobName: string;
  runStatus: 'PREPARING' | 'RUNNING' | 'SUCCESS' | 'FAILED' | 'FAILED_QUOTA';
  startedAt: string;
  completedAt: string;
}

export class JobsApi {
  async list() {
    return await checkAxiosResponse(axios.get<JobListItem[]>('/api/dask-jobs/shared/jobs/', axiosOptions()));
  }

  listSSE(cb: (value: JobListItem[]) => void) {
    return observeEndpoint<JobListItem[]>(`/api/dask-jobs/shared/jobs/sse`, cb);
  }

  async jobExecutions(jobId: string): Promise<JobExecution[] | undefined> {
    return await checkAxiosResponse(
      axios.get<JobExecution[]>(`/api/dask-jobs/my-executions?jobId=${encodeURIComponent(jobId)}`, axiosOptions(LONG_REQUEST_TIMEOUT))
    );
  }

  jobExecutionSSE(jobId: string, cb: (value: JobExecution[]) => void) {
    return observeEndpoint<JobExecution[]>(`/api/dask-jobs/my-executions/sse?jobId=${encodeURIComponent(jobId)}`, cb);
  }

  async currentExecutions(): Promise<JobExecution[] | undefined> {
    return await checkAxiosResponse(
      axios.get<JobExecution[]>(`/api/dask-jobs/my-executions?status=executing`, axiosOptions(LONG_REQUEST_TIMEOUT))
    );
  }

  currentExecutionsSSE(cb: (value: JobExecution[]) => void) {
    return observeEndpoint<JobExecution[]>(`/api/dask-jobs/my-executions/sse?status=executing`, cb);
  }

  async logs(executionId: string): Promise<{ logs: string } | undefined> {
    return await checkAxiosResponse(axios.get<{ logs: string }>(`/api/dask-jobs/my-executions/${executionId}/`, axiosOptions()));
  }

  async clusters(wbShortId: string): Promise<Cluster[] | undefined> {
    return await checkAxiosResponse(axios.get<Cluster[]>(`/api/dask/${wbShortId}/clusters`, axiosOptions()));
  }

  async updateJobConfig(model: EditJobModel): Promise<MstGenericResponse | undefined> {
    const { job, config } = model;
    return await extractValidationError(
      axios.post<MstGenericResponse>(`/api/dask-jobs/${job.wbShortId ?? 'shared'}/jobs/${job.id}/config`, { config }, axiosOptions())
    );
  }

  async updateJobEnabled(job: JobListItem, enabled: boolean): Promise<MstGenericResponse | undefined> {
    return await extractValidationError(
      axios.post<MstGenericResponse>(
        `/api/dask-jobs/${job.wbShortId ?? 'shared'}/jobs/${job.id}/enabled`,
        {
          enabled: !!enabled,
        },
        axiosOptions()
      )
    );
  }

  async deleteJob(job: JobListItem): Promise<{ ok: boolean } | undefined> {
    return await checkAxiosResponse(
      axios.delete<{ ok: boolean }>(`/api/dask-jobs/${job.wbShortId ?? 'shared'}/jobs/${job.id}`, axiosOptions())
    );
  }

  async getJobConfig(job: JobListItem) {
    return await checkAxiosResponse(
      axios.get<{ ok: boolean; config: ConfigSchemaFormModel['config']; configSchema: ConfigSchemaFormModel['configSchema'] }>(
        `/api/zoe/app-store/config/${job.id}`,
        axiosOptions()
      )
    );
  }

  async triggerJob(jobId: string, data: unknown): Promise<{ ok: boolean } | undefined> {
    return await checkAxiosResponse(axios.post<{ ok: boolean }>(`/api/dask-jobs/trigger`, { jobId, data }, axiosOptions()));
  }

  async interruptExecution(executionId: string): Promise<{ ok: boolean } | undefined> {
    return await checkAxiosResponse(axios.post<{ ok: boolean }>(`/api/dask-jobs/interrupt/${executionId}`, {}, axiosOptions()));
  }
}
